/**
 * `AccountDetailView` — single account drill-down.
 *
 * GET /internal/admin/accounts/:id on mount. The page is organised
 * into seven tabs backed by `?tab=<id>` in the URL (see
 * `./account-detail-tabs.ts`):
 *
 *   - Overview       — identity (name/status/is_internal) + quick
 *                      stats (sites, plan, created, last-active).
 *   - Users & access — support-login mint (ADR-0216). Future team
 *                      roster lands here.
 *   - Sites          — read-only list of the account's sites with
 *                      a per-row remove.
 *   - Plan & billing — subscription + billing events + usage +
 *                      entitlement overrides.
 *   - Features       — feature toggles (Agency / White-label from
 *                      ADR-0237 today).
 *   - Test           — the agent-smoke harness (ADR-0176) + the
 *                      per-site UCP checkout test runner. Both
 *                      issue real Stripe TEST-mode calls, so they
 *                      live together behind one tab the staff reads
 *                      as "exercise this merchant".
 *   - Danger zone    — destructive actions. Kept off the default
 *                      tab so no one trips a delete on first-load.
 *
 * Every tab panel stays mounted but is hidden with `hidden`/CSS when
 * it isn't the active one. That preserves unsaved form state in each
 * card when the staff member jumps between tabs — the cards all own
 * their own save buttons, so there's nothing to "warn-on-leave" at
 * the view level.
 *
 * After a successful PATCH or PUT, the local detail state is patched
 * in-place; we don't re-issue the GET (the response payloads carry
 * the canonical updated shape).
 */

import { ArrowLeft, Trash2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import type {
  AdminAccountDetailResponse,
  AdminAccountSite,
  AdminEntitlementOverride,
  AdminSubscription,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import type { SetView } from '../../app/view-router.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Pill, statusTone } from '../../components/atoms/Pill.js';
import { TypedConfirmDialog } from '../../components/atoms/TypedConfirmDialog.js';
import { useC } from '../../lib/theme.js';
import { formatIsoCompact, formatLastActive } from '../../lib/format.js';
import { AccountAddonsCard } from './AccountAddonsCard.js';
import { AccountEditCard } from './AccountEditCard.js';
import { AgencyFeatureCard } from './AgencyFeatureCard.js';
import { SitePlansCard } from './SitePlansCard.js';
import { SiteUsageCard } from './SiteUsageCard.js';
import { FeaturesInUseCard } from './FeaturesInUseCard.js';
import { SupportLoginCard } from './SupportLoginCard.js';
import { useAuth } from '../../app/auth-context.js';
import { EntitlementOverridesCard } from './EntitlementOverridesCard.js';
import { SmokeOptInCard } from './SmokeOptInCard.js';
import { SubscriptionCard } from './SubscriptionCard.js';
import { UcpCheckoutTestCard } from './UcpCheckoutTestCard.js';
import { BillingHistoryCard } from './BillingHistoryCard.js';
import { UsageCard } from './UsageCard.js';
import { AccountDetailTabs } from './AccountDetailTabs.js';
import {
  type AccountTabId,
  useAccountTab,
} from './account-detail-tabs.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

const PANEL_PREFIX = 'account-detail';

export interface AccountDetailViewProps {
  accountId: string;
  setView: SetView;
}

export function AccountDetailView({
  accountId,
  setView,
}: AccountDetailViewProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const auth = useAuth();
  const staffRole =
    auth.state.kind === 'authenticated' ? auth.state.staff.role : 'support';

  const { tab, setTab } = useAccountTab();

  const [data, setData] = useState<AdminAccountDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.getAccount(accountId);
        if (!cancelled) setData(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'not_found') {
          setError('Account not found.');
          return;
        }
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setError(`Could not load account${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, accountId]);

  const onAccountUpdated = (next: {
    name: string;
    status: string;
    is_internal: boolean;
    smoke_test_opt_in?: boolean;
  }): void => {
    setData((cur) =>
      cur === null
        ? cur
        : {
            ...cur,
            account: {
              ...cur.account,
              name: next.name,
              status: next.status,
              is_internal: next.is_internal,
              // ADR-0176 §1 — the PATCH response echoes the current
              // smoke_test_opt_in value; the toggle card patches it
              // in-place without a refetch. Falls back to the prior
              // value when the field isn't in the response (backwards-
              // compat with older server versions).
              smoke_test_opt_in:
                next.smoke_test_opt_in ?? cur.account.smoke_test_opt_in,
            },
          },
    );
  };

  const onSubscriptionAssigned = (next: AdminSubscription): void => {
    setData((cur) => (cur === null ? cur : { ...cur, subscription: next }));
  };

  // Patch only the `enforcement_enabled` field on the existing
  // subscription. The server upserts the `account_billing` shell row
  // on first flip, so an account with no plan goes from
  // `subscription: null` → a minimal-status subscription with
  // `enforcement_enabled` set. We re-fetch in that case rather than
  // synthesize a partial shape locally.
  const onEnforcementChanged = (enabled: boolean): void => {
    setData((cur) => {
      if (cur === null) return cur;
      if (cur.subscription === null) {
        // No subscription locally yet — the server may have just
        // created a shell row. Re-fetch so we have the canonical
        // shape; until then, keep the prior value.
        void (async (): Promise<void> => {
          try {
            const res = await api.getAccount(accountId);
            setData(res);
          } catch {
            // Best-effort refresh; the toggle still succeeded server-side.
          }
        })();
        return cur;
      }
      return {
        ...cur,
        subscription: { ...cur.subscription, enforcement_enabled: enabled },
      };
    });
  };

  const onOverridesUpdated = (
    next: readonly AdminEntitlementOverride[],
  ): void => {
    setData((cur) =>
      cur === null ? cur : { ...cur, entitlement_overrides: next },
    );
  };

  const onSiteDeleted = (siteId: string): void => {
    setData((cur) =>
      cur === null ? cur : { ...cur, sites: cur.sites.filter((s) => s.site_id !== siteId) },
    );
  };

  // After a successful account-delete we route back to the list and
  // surface a `?deleted=…` flag for AccountsListView to read into a
  // toast on its next render. Inline-state toasting (no global store)
  // matches the rest of the admin's pattern.
  const onAccountDeleted = (): void => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('deleted', accountId);
      window.history.replaceState({}, '', url.toString());
    }
    setView('accounts');
  };

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      <button
        type="button"
        onClick={() => setView('accounts')}
        className="flex items-center gap-1 mb-4"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          background: 'transparent',
          border: 0,
          padding: 0,
          minHeight: 40,
        }}
      >
        <ArrowLeft size={12} /> All accounts
      </button>

      {loading ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          Loading…
        </div>
      ) : error !== null ? (
        <Card style={{ padding: 16 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Load failed
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>{error}</div>
        </Card>
      ) : data !== null ? (
        <>
          <DetailHeader account={data.account} />
          <AccountDetailTabs
            value={tab}
            onChange={setTab}
            panelIdPrefix={PANEL_PREFIX}
          />
          <div className="grid grid-cols-1 gap-4">
            <TabPanel id="overview" active={tab}>
              <AccountEditCard
                account={data.account}
                onUpdated={onAccountUpdated}
              />
              <OverviewStats data={data} />
            </TabPanel>

            <TabPanel id="access" active={tab}>
              {/* ADR-0216 — one-time support-login link. Superadmin only;
                  the card renders null for 'support' role. Server-side
                  gate is the load-bearing one (403). */}
              <SupportLoginCard
                accountId={data.account.account_id}
                staffRole={staffRole}
              />
              <TeamRosterPlaceholder />
            </TabPanel>

            <TabPanel id="sites" active={tab}>
              <SitesCard
                accountId={data.account.account_id}
                sites={data.sites}
                onSiteDeleted={onSiteDeleted}
              />
            </TabPanel>

            <TabPanel id="billing" active={tab}>
              <SubscriptionCard
                accountId={data.account.account_id}
                subscription={data.subscription}
                billingCustomerRef={data.account.billing_customer_ref}
                onAssigned={onSubscriptionAssigned}
                onEnforcementChanged={onEnforcementChanged}
              />
              {/* ADR-0263 §2.2 — per-site plan assignment. */}
              <SitePlansCard accountId={data.account.account_id} />
              {/* ADR-0263 §2.7 — per-site current-month meters (page
                  loads, orders, assistant requests). */}
              <SiteUsageCard accountId={data.account.account_id} />
              {/* ADR-0263 §2.5 — account add-ons list + add + cancel. */}
              <AccountAddonsCard
                accountId={data.account.account_id}
                sites={data.sites}
              />
              {/* ADR-0263 S4 — features in use outside the plan (read-only). */}
              <FeaturesInUseCard accountId={data.account.account_id} />
              <BillingHistoryCard events={data.billing_events} />
              <UsageCard accountId={data.account.account_id} />
              <EntitlementOverridesCard
                accountId={data.account.account_id}
                overrides={data.entitlement_overrides}
                onUpdated={onOverridesUpdated}
              />
            </TabPanel>

            <TabPanel id="features" active={tab}>
              {/* ADR-0237 — Agency / White label feature toggles. */}
              <AgencyFeatureCard
                accountId={data.account.account_id}
                staffRole={staffRole}
              />
            </TabPanel>

            <TabPanel id="test" active={tab}>
              {/* ADR-0176 — agent smoke harness (seven-skill guided run
                  + ad-hoc read tests). Moved here from Features so this
                  tab reads as "exercise the merchant". */}
              <SmokeOptInCard
                accountId={data.account.account_id}
                optedIn={data.account.smoke_test_opt_in}
                sites={data.sites}
                onUpdated={(next): void => {
                  onAccountUpdated({
                    name: next.name,
                    status: next.status,
                    is_internal: next.is_internal,
                    smoke_test_opt_in: next.smoke_test_opt_in,
                  });
                }}
                setView={setView}
              />
              {/* ops2026 UCP checkout test — per-site Run button that
                  drives the full UCP checkout (create → ship → complete
                  tok_visa → refund). TEST-mode only; 6/hour/staff. */}
              <UcpCheckoutTestCard
                accountId={data.account.account_id}
                sites={data.sites}
              />
            </TabPanel>

            <TabPanel id="danger" active={tab}>
              <DangerZoneCard
                account={data.account}
                sites={data.sites}
                onDeleted={onAccountDeleted}
                onSuspendInstead={(): void => {
                  // Patch the account status to `suspended` via the
                  // existing PATCH; the response is the same shape
                  // `onAccountUpdated` consumes.
                  void (async (): Promise<void> => {
                    try {
                      const res = await api.updateAccount(data.account.account_id, {
                        status: 'suspended',
                      });
                      onAccountUpdated({
                        name: res.account.name,
                        status: res.account.status,
                        is_internal: res.account.is_internal,
                      });
                      // Jump to Overview so the fresh status pill is
                      // the first thing the staff member sees.
                      setTab('overview');
                    } catch {
                      // The DangerZoneCard's own error banner will be
                      // a re-attempt-via-the-edit-form story; we keep
                      // this branch quiet rather than double-surfacing.
                    }
                  })();
                }}
              />
            </TabPanel>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * Per-tab panel wrapper. We keep every panel in the DOM and toggle
 * `hidden` so unsaved form state inside each card survives a tab
 * switch; mounting + unmounting on `tab === id` would reset every
 * controlled input on each flip.
 */
function TabPanel({
  id,
  active,
  children,
}: {
  id: AccountTabId;
  active: AccountTabId;
  children: ReactNode;
}): JSX.Element {
  const isActive = id === active;
  return (
    <div
      role="tabpanel"
      id={`${PANEL_PREFIX}-panel-${id}`}
      aria-labelledby={`${PANEL_PREFIX}-tab-${id}`}
      hidden={!isActive}
      className={isActive ? 'grid grid-cols-1 gap-4' : undefined}
      // When `hidden` is set, `aria-hidden` is implied; the browser
      // skips the subtree for focus + a11y tree.
    >
      {children}
    </div>
  );
}

function DetailHeader({
  account,
}: {
  account: AdminAccountDetailResponse['account'];
}): JSX.Element {
  const C = useC();
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 10,
            wordBreak: 'break-all',
          }}
        >
          Account · {account.account_id}
        </div>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(28px, 6vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
            margin: 0,
            wordBreak: 'break-word',
          }}
        >
          {account.name}
        </h1>
        <div
          className="flex items-center flex-wrap"
          style={{
            marginTop: 10,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            gap: 14,
          }}
        >
          <HeaderMeta
            label="Signed up"
            value={formatIsoCompact(account.created_at)}
          />
          <HeaderMeta
            label="Last login"
            value={formatLastActive(account.last_login_at)}
            dim={account.last_login_at === null}
            title="Most recent dashboard login by any user on this account"
          />
          <HeaderMeta
            label="Last active"
            value={formatLastActive(account.last_active_at)}
            dim={account.last_active_at === null}
            title="Most recent agent activity (audit record) on this account"
          />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone={statusTone(account.status)}>{account.status}</Pill>
        {account.is_internal ? <Pill tone="amber">Internal</Pill> : null}
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 1,
          }}
        >
          {account.region}
        </span>
      </div>
    </div>
  );
}

function HeaderMeta({
  label,
  value,
  dim = false,
  title,
}: {
  label: string;
  value: string;
  dim?: boolean;
  /** Optional disambiguation tooltip — used for the "Last login" /
   *  "Last active" pair, which sit next to each other but measure
   *  different signals (human dashboard login vs. agent traffic). */
  title?: string;
}): JSX.Element {
  const C = useC();
  return (
    <span className="flex items-baseline gap-2" title={title}>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: dim ? C.dim : C.body,
        }}
      >
        {value}
      </span>
    </span>
  );
}

/**
 * Four-up stats block for the Overview tab. Everything here is read
 * from `data` the parent already has — no extra fetches.
 */
function OverviewStats({
  data,
}: {
  data: AdminAccountDetailResponse;
}): JSX.Element {
  const C = useC();
  const plan =
    data.subscription !== null && data.subscription.plan_name !== null
      ? data.subscription.plan_name
      : '—';
  const enforcement =
    data.subscription !== null
      ? data.subscription.enforcement_enabled
        ? 'enforced'
        : 'advisory'
      : '—';
  // ADR-0263 §5.3 (S6) — "Billing: own" vs "Billing: paid by <agency>".
  const billing_value =
    data.agency_paid === null
      ? 'Own'
      : `Paid by ${data.agency_paid.agency_name}`;
  const stats: ReadonlyArray<{ label: string; value: string }> = [
    { label: 'Sites', value: String(data.sites.length) },
    { label: 'Plan', value: plan },
    { label: 'Enforcement', value: enforcement },
    { label: 'Region', value: data.account.region },
    { label: 'Billing', value: billing_value },
  ];
  return (
    <Card style={{ padding: 20 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 12,
        }}
      >
        At a glance
      </div>
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
      >
        {stats.map((s) => (
          <div
            key={s.label}
            style={{
              background: C.surface2,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 2,
              padding: '10px 12px',
            }}
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              {s.label}
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 15,
                color: C.text,
                fontWeight: 500,
                wordBreak: 'break-word',
              }}
            >
              {s.value}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Users & access tab currently exposes the support-login mint only.
 * The full per-account team roster is a future surface (the API
 * side would be `GET /internal/admin/accounts/:id/users`, not shipped
 * yet). We leave an explicit placeholder so the tab isn't visually
 * empty when the account has no support-login capability (e.g. a
 * staff member on the 'support' role, which hides SupportLoginCard).
 */
function TeamRosterPlaceholder(): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 20 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Team roster
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        Per-account user list is not shipped yet. For today, cross-reference
        the Team page by account id.
      </div>
    </Card>
  );
}

interface SitesCardProps {
  accountId: string;
  sites: readonly AdminAccountSite[];
  onSiteDeleted: (siteId: string) => void;
}

function SitesCard({ accountId, sites, onSiteDeleted }: SitesCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [confirmSite, setConfirmSite] = useState<AdminAccountSite | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doDelete = async (): Promise<void> => {
    if (confirmSite === null || pending) return;
    setPending(true);
    setError(null);
    try {
      await api.deleteSite(accountId, confirmSite.site_id);
      onSiteDeleted(confirmSite.site_id);
      setConfirmSite(null);
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setError(`Could not remove domain${reason}.`);
    } finally {
      setPending(false);
    }
  };

  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between mb-3">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Sites
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            letterSpacing: 1,
          }}
        >
          {sites.length} total
        </div>
      </div>
      {sites.length === 0 ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
          }}
        >
          This account has no sites yet.
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-2 list-none p-0 m-0">
          {sites.map((s) => (
            <SiteRow
              key={s.site_id}
              site={s}
              onDelete={(): void => {
                setError(null);
                setConfirmSite(s);
              }}
            />
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={confirmSite !== null}
        title={confirmSite !== null ? `Remove ${confirmSite.domain}?` : 'Remove domain?'}
        body={
          <>
            This soft-deletes the site row — the embed will stop being
            served and the domain becomes re-addable later. Audit history
            and signed mandate records are retained.
            {error !== null ? (
              <div
                style={{
                  marginTop: 12,
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: C.coralFg,
                }}
              >
                {error}
              </div>
            ) : null}
          </>
        }
        confirmLabel="Remove domain"
        tone="coral"
        pending={pending}
        onConfirm={(): void => void doDelete()}
        onCancel={(): void => {
          if (pending) return;
          setConfirmSite(null);
          setError(null);
        }}
      />
    </Card>
  );
}

/**
 * One site row — a flex-row on sm+, a stacked card on mobile.
 * On mobile: domain on its own line, status + meta + delete in a row
 * below. On sm+: domain + meta on the left, status + date + delete on
 * the right.
 */
function SiteRow({
  site,
  onDelete,
}: {
  site: AdminAccountSite;
  onDelete: () => void;
}): JSX.Element {
  const C = useC();
  return (
    <li
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 px-3 py-2"
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
      }}
    >
      <div className="min-w-0">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            wordBreak: 'break-all',
          }}
          title={site.domain}
        >
          {site.domain}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            marginTop: 2,
            wordBreak: 'break-all',
          }}
        >
          {site.site_id} · {site.region}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-between sm:justify-end">
        <div className="flex items-center gap-2">
          <Pill tone={statusTone(site.status)}>{site.status}</Pill>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              whiteSpace: 'nowrap',
            }}
          >
            {formatIsoCompact(site.created_at)}
          </span>
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="flex items-center gap-1"
          style={{
            background: 'transparent',
            color: C.coralFg,
            fontFamily: sansFont,
            fontSize: 12,
            padding: '6px 10px',
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
            minHeight: 40,
          }}
          aria-label={`Delete domain ${site.domain}`}
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </li>
  );
}

interface DangerZoneCardProps {
  account: AdminAccountDetailResponse['account'];
  sites: readonly AdminAccountSite[];
  onDeleted: () => void;
  onSuspendInstead: () => void;
}

/**
 * The destructive-actions card. Today this is just "delete account",
 * but the surface is named for what it IS (a danger zone) rather than
 * what it CONTAINS, so future destructive actions (force-rotate signing
 * key, purge cached agent cards, etc.) can land here without a rename.
 *
 * Delete-account uses the typed-confirm gate: the staff member must
 * type the account's primary site domain — or, if the account has no
 * sites yet, its `account_id` — to enable the Delete button. The
 * server validates the same string against its own source-of-truth
 * (the URL param or the site row), so a hand-crafted `confirm` value
 * cannot bypass the gate.
 *
 * 409 `account_has_audit_history` → the audit-record table's append-only
 * trigger + ON DELETE RESTRICT forbid the hard delete; we surface the
 * server reason and offer "Suspend instead" which fires the existing
 * PATCH path with `status: 'suspended'`.
 */
function DangerZoneCard({
  account,
  sites,
  onDeleted,
  onSuspendInstead,
}: DangerZoneCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditBlocked, setAuditBlocked] = useState(false);

  // Server contract: confirm MUST equal the primary site domain when
  // the account has sites; otherwise the account_id. "Primary" is the
  // earliest-created site row — the API returns sites already sorted
  // by created_at ascending, so we take the first.
  const expected = sites.length > 0 ? sites[0]!.domain : account.account_id;

  const doDelete = async (): Promise<void> => {
    if (pending) return;
    setPending(true);
    setError(null);
    setAuditBlocked(false);
    try {
      await api.deleteAccount(account.account_id, expected);
      onDeleted();
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.kind === 'conflict' &&
        err.server_reason === 'account_has_audit_history'
      ) {
        setAuditBlocked(true);
        setError(
          "This account has audit history and can't be deleted — suspend it instead.",
        );
      } else {
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? `: ${err.server_reason}`
            : '';
        setError(`Could not delete account${reason}.`);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Card
      style={{
        padding: 20,
        // Border tint signals "this card is dangerous" without the
        // wall-of-coral look of an alert. Same idea as the GitHub
        // "Danger zone" panel. Card uses the `border` shorthand so we
        // override with a full `border` value (not just borderColor).
        border: `1px solid ${C.coralFg}`,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.coralFg,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 12,
        }}
      >
        Danger zone
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              fontWeight: 500,
              marginBottom: 4,
            }}
          >
            Delete account
          </div>
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.5,
            }}
          >
            Permanently removes the account, every owned site, and the
            local-managed billing row. Audit records are append-only —
            accounts with audit history can't be hard-deleted; suspend
            them instead.
          </div>
        </div>
        <button
          type="button"
          onClick={(): void => setOpen(true)}
          className="flex items-center gap-2 flex-shrink-0 justify-center"
          style={{
            background: 'transparent',
            color: C.coralFg,
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 600,
            padding: '10px 14px',
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
            minHeight: 44,
          }}
        >
          <Trash2 size={13} /> Delete account
        </button>
      </div>

      <TypedConfirmDialog
        open={open}
        title={`Delete ${account.name}?`}
        body={
          <>
            This permanently removes the account, its sites
            ({sites.length}), and the local-managed billing row. There is
            no undo. To confirm, type the account's primary{' '}
            {sites.length > 0 ? 'domain' : 'account ID'} below.
          </>
        }
        expected={expected}
        inputLabel={
          <>
            Type <span style={{ color: C.text }}>{expected}</span> to confirm
          </>
        }
        confirmLabel="Delete account"
        pending={pending}
        error={error}
        footer={
          auditBlocked ? (
            <button
              type="button"
              onClick={(): void => {
                setOpen(false);
                setError(null);
                setAuditBlocked(false);
                onSuspendInstead();
              }}
              style={{
                background: C.surface2,
                color: C.text,
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 500,
                padding: '8px 14px',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                width: '100%',
              }}
            >
              Suspend account instead
            </button>
          ) : null
        }
        onConfirm={(): void => void doDelete()}
        onCancel={(): void => {
          if (pending) return;
          setOpen(false);
          setError(null);
          setAuditBlocked(false);
        }}
      />
    </Card>
  );
}
